import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PaymentRequestService } from '../services/payment.request.service';
import { SearchService } from '../services/search.service';
import { PaymentStore } from '@app/features/payment/public/report/store/payment-report.store';
import { PaymentRequestModel } from '@app/components/models/payment.request.model';
import { User } from '@app/components/models/user.mode';
import { AssetStore } from '@app/stores/asset.store';
import { AuthStore } from '@app/stores/auth.store';

interface GuardContext {
  paymentId: string | null;
  quoteId: string | null;
  receiverShortId: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentReportGuard implements CanActivate {
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly paymentRequestService = inject(PaymentRequestService);
  private readonly searchService = inject(SearchService);
  private readonly paymentStore = inject(PaymentStore);
  private readonly assetStore = inject(AssetStore);

  private log(message: string, isError = false): void {
    const color = isError ? '#ff0000' : '#00ff00';
    console.log(
      `%c ${message} `,
      `background: ${color}; color: #ffffff; padding: 2px 4px; border-radius: 3px; font-weight: bold;`,
    );
  }

  private redirectWithError(message: string, reason: string): boolean {
    this.log(message, true);
    this.router.navigate(['/error'], { queryParams: { reason } });
    return false;
  }

  private getGuardContext(route: ActivatedRouteSnapshot): GuardContext {
    return {
      paymentId: route.queryParamMap.get('paymentId') || null,
      quoteId: route.queryParamMap.get('quoteId') || null,
      receiverShortId: route.paramMap.get('receiverShortId') || null,
    };
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const context = this.getGuardContext(route);

    if (!context.paymentId && !context.receiverShortId) {
      return this.redirectWithError('receiver-short-id-not-found', 'receiver-short-id-not-found');
    }

    try {
      await this.authStore.asyncCurrentUser();
      this.paymentStore.updatePayerAuthentication(this.authStore.authenticated());
    } catch (error) {
      console.log(error, 'user not authenticated');
    }

    if (this.paymentStore.isAccessRestrictedBilling()) {
      return this.redirectWithError('restricted-billing-target-user', 'restricted-billing-target-user');
    }

    if (context.paymentId && !(await this.loadPaymentRequest(context.paymentId))) {
      return this.redirectWithError('payment-not-loaded', 'payment-not-loaded');
    }

    const receiverIdToUse =
      context.receiverShortId || this.paymentStore.paymentRequest?.()?.bill?.receiverUser?.shortId;
    if (receiverIdToUse && !(await this.loadReceiver(receiverIdToUse))) {
      return this.redirectWithError('receiver-not-found', 'receiver-not-found');
    }

    if (context.quoteId) {
      await this.loadQuote(context.quoteId);
    }

    return await this.initializeContext(context.paymentId, context.quoteId);
  }

  private async loadPaymentRequest(paymentId: string): Promise<boolean> {
    try {
      const paymentRequest = await firstValueFrom(this.paymentRequestService.getPaymentById(paymentId));
      if (!paymentRequest) return false;

      this.log('payment-id-found');
      await this.paymentStore.updatePaymentRequest(paymentRequest as PaymentRequestModel);

      if (this.paymentStore.isPaymentRequestReported()) {
        return this.redirectWithError('payment-id-already-reported', 'payment-id-already-reported');
      }

      this.log('payment-id-found and payment-request-not-reported');
      return true;
    } catch {
      return this.redirectWithError('payment-id-not-found', 'payment-id-not-found');
    }
  }

  private async loadReceiver(shortId: string): Promise<boolean> {
    try {
      const user = await firstValueFrom(this.searchService.getPublicProfileByShortId(shortId));

      if (!user) return false;

      await this.paymentStore.setTargetUser(user as User);

      if (this.paymentStore.ifNotPaymentRequestHasSameTargetUser()) {
        return this.redirectWithError(
          'payment-request-has-not-same-target-user',
          'payment-request-has-not-same-target-user',
        );
      }

      this.log('receiver-short-id-found');
      return true;
    } catch {
      return this.redirectWithError('receiver-short-id-not-found', 'receiver-short-id-not-found');
    }
  }

  private async loadQuote(quoteId: string): Promise<boolean> {
    try {
      const quote = await firstValueFrom(this.paymentStore.getUnusedQuoteById(quoteId));
      if (!quote) return false;

      const paymentRequest = this.paymentStore.paymentRequest();

      if (paymentRequest.amountNative) {
        const { blockchainAssetId, amountNative } = paymentRequest;

        const isQuoteValid = quote.from.id === blockchainAssetId && quote.requestedAmount === amountNative;

        if (!isQuoteValid) return false;
      }

      this.paymentStore.setPaymentDetailsFromQuote(quote);
      this.log('quote-id-found');
      return true;
    } catch {
      return this.redirectWithError('quote-id-not-found', 'quote-id-not-found');
    }
  }

  private async initializeContext(paymentId: string | null, quoteId: string | null): Promise<boolean> {
    await Promise.all([
      this.assetStore.loadAssets(),
      this.paymentStore.fetchReceiverAcceptedAssets(),
      this.paymentStore.fetchUserWallet(),
    ]);

    if (!(await this.paymentStore.asyncHasConnections())) {
      return this.redirectWithError('thisUserDoesNotHaveAWallet', 'thisUserDoesNotHaveAWallet');
    }

    if (!(await this.paymentStore.asyncHasAcceptedAssets())) {
      return this.redirectWithError('noSettlementAssetForReceiver', 'noSettlementAssetForReceiver');
    }

    this.paymentStore.initializeReceiverAssets();

    if (!quoteId) {
      this.paymentStore.handleInitialPayingAssetSelection();
    }

    if (!paymentId && !quoteId) {
      const defaultAsset = this.paymentStore.receiverAcceptedAssets()[0]?.assetTicker;
      if (defaultAsset) {
        this.paymentStore.handleSettlementAssetSelection(defaultAsset);
      }
    }

    await this.paymentStore.changeTargetCurrency();

    return true;
  }
}
